/**
 * ImageDisplay Component
 *
 * Reusable component for displaying node images with proper error handling
 */

import { ImageOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/utils/cn'

export interface ImageDisplayProps {
  src: string
  alt: string
  className?: string
  aspectRatio?: 'auto' | 'square' | 'portrait' | 'landscape'
  fallback?: React.ReactNode
  onError?: () => void
}

export function ImageDisplay({
  src,
  alt,
  className,
  aspectRatio = 'auto',
  fallback,
  onError,
}: ImageDisplayProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const handleLoad = () => {
    setLoading(false)
  }

  const handleError = () => {
    setLoading(false)
    setError(true)
    onError?.()
  }

  const aspectClasses = {
    auto: 'aspect-auto',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
    landscape: 'aspect-[4/3]',
  }

  if (error) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-muted/50 border border-border rounded-lg p-4',
          aspectClasses[aspectRatio],
          className
        )}
      >
        {fallback || (
          <>
            <ImageOff className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground text-center">Image not available</p>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={cn('relative rounded-lg overflow-hidden bg-muted/20', className)}>
      {loading && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-muted/50',
            aspectClasses[aspectRatio]
          )}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-200',
          loading ? 'opacity-0' : 'opacity-100'
        )}
      />
    </div>
  )
}

/**
 * Thumbnail version for small images
 */
export function ImageThumbnail({
  src,
  alt,
  size = 'md',
  onClick,
}: {
  src: string
  alt: string
  size?: 'sm' | 'md' | 'lg'
  onClick?: () => void
}) {
  const sizeClasses = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
  }

  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden border border-border bg-muted/20',
        sizeClasses[size],
        onClick && 'cursor-pointer hover:border-primary transition-colors'
      )}
      onClick={onClick}
    >
      <ImageDisplay src={src} alt={alt} aspectRatio="square" />
    </div>
  )
}

/**
 * Image gallery for multiple images
 */
export function ImageGallery({ images }: { images: Array<{ src: string; alt: string }> }) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  if (images.length === 0) return null

  if (images.length === 1) {
    return <ImageDisplay src={images[0].src} alt={images[0].alt} className="w-full" />
  }

  return (
    <div className="space-y-2">
      {/* Main image */}
      <ImageDisplay
        src={images[selectedIndex].src}
        alt={images[selectedIndex].alt}
        className="w-full"
      />

      {/* Thumbnails */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {images.map((img, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className={cn(
              'flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors',
              selectedIndex === index ? 'border-primary' : 'border-border hover:border-primary/50'
            )}
          >
            <div className="h-16 w-16 overflow-hidden">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
